import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface CartItem {
  id: string;
  name: string;
  size: string;
  price: number;
  quantity: number;
  productId?: string;
  image?: string;
}

interface PriceUpdate {
  itemId: string;
  oldPrice: number;
  newPrice: number;
  productName: string;
}

export const useLiveCartPricing = (items: CartItem[]) => {
  const [updatedItems, setUpdatedItems] = useState<CartItem[]>(items);
  const [priceChanges, setPriceChanges] = useState<PriceUpdate[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchLivePrices = async () => {
      if (items.length === 0) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        
        // Extract unique product IDs
        const productIds = items
          .filter(item => item.productId)
          .map(item => item.productId);

        if (productIds.length === 0) {
          setUpdatedItems(items);
          setIsLoading(false);
          return;
        }

        // Fetch current prices from database
        const { data: products, error } = await supabase
          .from('products')
          .select('id, price_25g, price_50g, slug')
          .in('id', productIds);

        if (error) throw error;

        const changes: PriceUpdate[] = [];
        const updated = items.map(item => {
          if (!item.productId) return item;

          const product = products?.find(p => p.id === item.productId);
          if (!product) return item;

          // Determine correct price based on size
          const livePrice = item.size === '25g' 
            ? Number(product.price_25g) 
            : Number(product.price_50g);

          // Check if price changed
          if (Math.abs(livePrice - item.price) > 0.01) {
            changes.push({
              itemId: item.id,
              oldPrice: item.price,
              newPrice: livePrice,
              productName: item.name,
            });

            return { ...item, price: livePrice };
          }

          return item;
        });

        setUpdatedItems(updated);
        setPriceChanges(changes);
      } catch (err) {
        console.error('Error fetching live prices:', err);
        setUpdatedItems(items);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLivePrices();
  }, [items]);

  const total = updatedItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

  return {
    items: updatedItems,
    priceChanges,
    total,
    isLoading,
    hasPriceChanges: priceChanges.length > 0,
  };
};
